import { useEffect, useState, useCallback } from 'react';
import { useCanvasStore } from '../stores/canvasStore.js';

interface HighlightConfig {
  widgetId?: string;
  connectionId?: string;
  color: string;
  duration: number;
  type: 'pulse' | 'glow' | 'border';
}

export function useCanvasHighlight() {
  const [highlights, setHighlights] = useState<Map<string, HighlightConfig>>(new Map());
  const activeCanvas = useCanvasStore(state => state.getActiveCanvas());
  
  // Remove a highlight  
  const removeHighlight = useCallback((id: string) => {
    setHighlights(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Add a highlight
  const addHighlight = useCallback((id: string, config: HighlightConfig) => {
    setHighlights(prev => {
      const next = new Map(prev);
      next.set(id, config);
      return next;
    });
    
    // Auto-remove after duration
    setTimeout(() => {
      removeHighlight(id);
    }, config.duration);
  }, [removeHighlight]);
  
  // Highlight a widget
  const highlightWidget = useCallback((widgetId: string, color = '#2196F3', duration = 2000, type: HighlightConfig['type'] = 'pulse') => {
    addHighlight(`widget-${widgetId}`, {
      widgetId,
      color,
      duration,
      type
    });
  }, [addHighlight]);
  
  // Highlight a connection
  const highlightConnection = useCallback((connectionId: string, color = '#FF9800', duration = 2000, type: HighlightConfig['type'] = 'glow') => {
    addHighlight(`connection-${connectionId}`, {
      connectionId,
      color,
      duration,
      type
    });
  }, [addHighlight]);
  
  // Highlight all widgets of a specific type
  const highlightWidgetsByType = useCallback((widgetType: string, color = '#4CAF50', duration = 2000, stagger = 100) => {
    if (!activeCanvas) return;
    
    const widgetsOfType = activeCanvas.widgets.filter(w => w.type === widgetType);
    widgetsOfType.forEach((widget, index) => {
      setTimeout(() => {
        highlightWidget(widget.id, color, duration);
      }, index * stagger);
    });
  }, [activeCanvas, highlightWidget]);
  
  // Highlight a path through the canvas
  const highlightPath = useCallback((widgetIds: string[], color = '#9C27B0', duration = 3000, stagger = 200) => {
    if (!activeCanvas) return;
    
    widgetIds.forEach((widgetId, index) => {
      setTimeout(() => {
        highlightWidget(widgetId, color, duration, 'glow');
        
        // Also highlight connections between consecutive widgets
        if (index < widgetIds.length - 1) {
          const nextWidgetId = widgetIds[index + 1];
          const connection = activeCanvas.connections.find(conn => 
            (conn.source.widgetId === widgetId && conn.target.widgetId === nextWidgetId) ||
            (conn.source.widgetId === nextWidgetId && conn.target.widgetId === widgetId)
          );
          
          if (connection) {
            setTimeout(() => {
              highlightConnection(connection.id, color, duration);
            }, stagger / 2);
          }
        }
      }, index * stagger);
    });
  }, [activeCanvas, highlightWidget, highlightConnection]);
  
  // Get highlight for a specific widget
  const getWidgetHighlight = useCallback((widgetId: string): HighlightConfig | undefined => {
    return highlights.get(`widget-${widgetId}`);
  }, [highlights]);
  
  // Get highlight for a specific connection
  const getConnectionHighlight = useCallback((connectionId: string): HighlightConfig | undefined => {
    return highlights.get(`connection-${connectionId}`);
  }, [highlights]);
  
  // Clear all highlights
  const clearAllHighlights = useCallback(() => {
    setHighlights(new Map());
  }, []);
  
  return {
    highlights,
    highlightWidget,
    highlightConnection,
    highlightWidgetsByType,
    highlightPath,
    getWidgetHighlight,
    getConnectionHighlight,
    clearAllHighlights
  };
}